'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import AdminCrudModal from '../../components/AdminCrudModal';
import {
  normalizeErrorMessage,
  normalizeFieldErrors,
  requestWithFallback,
  unpackCollection,
} from '../../services/adminCrudApi';
import {
  validatePasienForm,
  normalizeJenisKelaminForForm,
  displayJenisKelamin,
} from '../../services/adminMasterValidation';
import './Pasien.css';

const emptyForm = () => ({
  nama: '',
  tanggal_lahir: '',
  jenis_kelamin: '',
  no_telepon: '',
  email: '',
  golongan_darah: '',
  no_identitas: '',
  alamat: '',
  password: '',
});

function buildPasienPayload(form, isEdit = false) {
  const payload = {
    nama: String(form.nama || '').trim(),
    tanggal_lahir: form.tanggal_lahir,
    jenis_kelamin: form.jenis_kelamin === 'L' ? 'Laki-laki' : form.jenis_kelamin === 'P' ? 'Perempuan' : form.jenis_kelamin,
    no_telepon: String(form.no_telepon || '').trim(),
    no_identitas: String(form.no_identitas || '').replace(/\D/g, '').slice(0, 16),
    alamat: String(form.alamat || '').trim(),
  };

  if (form.email?.trim()) payload.email = form.email.trim();
  if (form.golongan_darah?.trim()) payload.golongan_darah = String(form.golongan_darah).toUpperCase().trim();

  if (form.password?.trim()) {
    payload.password = form.password.trim();
  } else if (!isEdit) {
    payload.password = 'password123';
  }

  return payload;
}

export default function Pasien() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [form, setForm] = useState(emptyForm);

  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  // Delete modal states
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [notice, setNotice] = useState('');

  const [filterSearchInput, setFilterSearchInput] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterJk, setFilterJk] = useState('');
  const [filterGoldar, setFilterGoldar] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setFilterSearch(filterSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [filterSearchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterSearch, filterJk, filterGoldar, itemsPerPage]);

  const fetchPatients = useCallback(async (page = 1) => {
    setLoading(true);
    const params = {
      page: page,
      per_page: itemsPerPage
    };
    if (filterSearch) params.search = filterSearch;
    if (filterJk) params.jenis_kelamin = filterJk;
    if (filterGoldar) params.golongan_darah = filterGoldar;

    try {
      const res = await requestWithFallback([
        { method: 'get', url: '/admin/pasien', params },
      ]);
      
      const data = res.data;
      if (data.data && Array.isArray(data.data)) {
        setPatients(data.data);
        setTotalItems(data.total || data.data.length);
        setTotalPages(data.last_page || Math.ceil((data.total || data.data.length) / itemsPerPage));
        setCurrentPage(data.current_page || page);
      } else {
        const unpacked = unpackCollection(data);
        setPatients(unpacked);
        setTotalItems(unpacked.length);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Fetch Pasien Error:', err);
      setPatients([]);
      setTotalItems(0);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  }, [filterSearch, filterJk, filterGoldar, itemsPerPage]);

  useEffect(() => {
    fetchPatients(currentPage);
  }, [fetchPatients, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const hitungUmur = (tanggal_lahir) => {
    if (!tanggal_lahir) return '-';
    const today = new Date();
    const lahir = new Date(tanggal_lahir);
    let age = today.getFullYear() - lahir.getFullYear();
    const m = today.getMonth() - lahir.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < lahir.getDate())) {
      age--;
    }
    return age;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Filter khusus NIK: Hanya terima angka dan maksimal 16 karakter
    if (name === 'no_identitas') {
      const onlyNums = value.replace(/\D/g, '').slice(0, 16);
      setForm((prev) => ({ ...prev, [name]: onlyNums }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditId(null);
    setErrors({});
    setSubmitError('');
  };

  const resetFilters = () => {
    setFilterSearchInput('');
    setFilterSearch('');
    setFilterJk('');
    setFilterGoldar('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setErrors({});

    let clientErrors = validatePasienForm ? validatePasienForm(form) : {};

    // Validasi tambahan khusus NIK (harus pas 16 digit)
    if (form.no_identitas && form.no_identitas.length !== 16) {
      clientErrors = {
        ...clientErrors,
        no_identitas: 'NIK / No. Identitas harus terdiri dari tepat 16 digit angka.',
      };
    }

    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    const payload = buildPasienPayload(form, Boolean(editId));
    setSubmitLoading(true);
    try {
      if (editId) {
        await requestWithFallback([
          { method: 'put', url: `/admin/pasien/${editId}`, data: payload },
        ]);
      } else {
        await requestWithFallback([
          { method: 'post', url: '/admin/pasien', data: payload },
        ]);
      }
      await fetchPatients(currentPage);
      setShowForm(false);
      setNotice(editId ? 'Data pasien berhasil diperbarui.' : 'Data pasien berhasil ditambahkan.');
      resetForm();
    } catch (err) {
      console.error('Submit Error:', err.response?.data);
      const fieldErrors = normalizeFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else setSubmitError(normalizeErrorMessage(err, 'Gagal menyimpan data pasien.'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (p) => {
    setNotice('');
    const formattedDate = p.tanggal_lahir ? String(p.tanggal_lahir).substring(0, 10) : '';
    const jk = p.jenis_kelamin === 'Laki-laki' ? 'L' : p.jenis_kelamin === 'Perempuan' ? 'P' : normalizeJenisKelaminForForm(p.jenis_kelamin);
    setForm({
      nama: p.nama ?? '',
      tanggal_lahir: formattedDate,
      jenis_kelamin: jk,
      no_telepon: p.no_telepon ?? '',
      email: p.email ?? '',
      golongan_darah: p.golongan_darah ? String(p.golongan_darah).toUpperCase() : '',
      no_identitas: p.no_identitas ? String(p.no_identitas).replace(/\D/g, '').slice(0, 16) : '',
      alamat: p.alamat ?? '',
      password: '',
    });
    setErrors({});
    setSubmitError('');
    setEditId(p.id);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    setDeleteError('');

    try {
      await requestWithFallback([
        { method: 'delete', url: `/admin/pasien/${deleteTarget.id}` },
      ]);
      
      const newPage = (patients.length === 1 && currentPage > 1) ? currentPage - 1 : currentPage;
      await fetchPatients(newPage);
      
      setDeleteTarget(null);
      setNotice('Data pasien berhasil dihapus.');
    } catch (err) {
      console.error('Delete Error:', err);
      const backendMessage = err?.response?.data?.message || err?.data?.message || err?.message;
      setDeleteError(typeof backendMessage === 'string' ? backendMessage : 'Gagal menghapus data pasien.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = (pasien) => {
    setNotice('');
    setDeleteTarget(pasien);
    setDeleteError('');
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteError('');
  };

  const renderFieldError = (field) =>
    errors[field] ? <small style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors[field][0] ?? errors[field]}</small> : null;

  const formatPasienId = (id, kode) => {
    if (kode) return kode;
    if (!id) return 'PSN-0000';
    return `PSN-${String(id).padStart(4, '0')}`;
  };

  return (
    <AdminLayout title="">

      {/* ── HEADER HALAMAN ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#111827', margin: 0 }}>Manajemen Pasien</h1>
          <p style={{ color: '#6B7280', margin: '4px 0 0', fontSize: '14px' }}>Kelola rekam data pasien dan riwayat informasi medis</p>
        </div>
        <button 
          onClick={() => { 
            setNotice('');
            setShowForm(true); 
            resetForm(); 
          }}
          style={btnPrimaryStyle}
        >
          + Tambah Pasien
        </button>
      </div>

      {/* ── NOTIFIKASI ── */}
      {notice && (
        <div style={{ padding: '12px 16px', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '10px', fontSize: '14px', marginBottom: '20px', border: '1px solid #A7F3D0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{notice}</span>
          <button onClick={() => setNotice('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46', fontWeight: 'bold' }}>✕</button>
        </div>
      )}

      {/* ── TOOLBAR & FILTER ── */}
      <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '16px 20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', flex: '1', minWidth: '280px' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label htmlFor="pasien-filter-search" style={labelStyle}>Cari Pasien</label>
            <input
              id="pasien-filter-search"
              type="search"
              placeholder="Cari ID Pasien, Nama, NIK..."
              value={filterSearchInput}
              onChange={(e) => setFilterSearchInput(e.target.value)}
              autoComplete="off"
              style={inputStyle}
            />
          </div>
          <div style={{ width: '150px' }}>
            <label htmlFor="pasien-filter-jk" style={labelStyle}>Jenis Kelamin</label>
            <select
              id="pasien-filter-jk"
              value={filterJk}
              onChange={(e) => setFilterJk(e.target.value)}
              style={inputStyle}
            >
              <option value="">Semua</option>
              <option value="Laki-laki">Laki-laki</option>
              <option value="Perempuan">Perempuan</option>
            </select>
          </div>
          <div style={{ width: '130px' }}>
            <label htmlFor="pasien-filter-goldar" style={labelStyle}>Gol. Darah</label>
            <select
              id="pasien-filter-goldar"
              value={filterGoldar}
              onChange={(e) => setFilterGoldar(e.target.value)}
              style={inputStyle}
            >
              <option value="">Semua</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="AB">AB</option>
              <option value="O">O</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button type="button" onClick={resetFilters} style={btnSecondaryStyle}>
            Reset Filter
          </button>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', backgroundColor: '#F3F4F6', padding: '8px 14px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
            Total: {totalItems}
          </span>
        </div>
      </div>

      {/* ── TABEL DATA PASIEN ── */}
      <div style={{ background: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: '20px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6B7280', fontSize: '14px' }}>⏳ Memuat data pasien...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  <th style={{ ...thStyle, width: '50px', textAlign: 'center' }}>No</th>
                  <th style={{ ...thStyle, width: '130px' }}>ID Pasien</th>
                  <th style={thStyle}>Informasi Pasien</th>
                  <th style={thStyle}>Umur</th>
                  <th style={thStyle}>JK</th>
                  <th style={thStyle}>No. Telepon</th>
                  <th style={thStyle}>Gol. Darah</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {patients.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '48px 20px', color: '#9CA3AF', fontSize: '14px' }}>
                      Tidak ada data pasien
                    </td>
                  </tr>
                ) : (
                  patients.map((p, i) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600', color: '#6B7280' }}>
                        {(currentPage - 1) * itemsPerPage + i + 1}
                      </td>
                      <td style={tdStyle}>
                        <span style={idBadgeStyle}>
                          {formatPasienId(p.id, p.kode_pasien || p.no_rekam_medis)}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: '600', color: '#111827' }}>{p.nama}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          NIK: {p.no_identitas || '-'} {p.email ? `• ${p.email}` : ''}
                        </div>
                      </td>
                      <td style={tdStyle}>{hitungUmur(p.tanggal_lahir)} thn</td>
                      <td style={tdStyle}>
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '6px', 
                          fontSize: '12px', 
                          fontWeight: '600',
                          backgroundColor: p.jenis_kelamin === 'Laki-laki' || p.jenis_kelamin === 'L' ? '#EFF6FF' : '#FCE7F3',
                          color: p.jenis_kelamin === 'Laki-laki' || p.jenis_kelamin === 'L' ? '#1E40AF' : '#9D174D'
                        }}>
                          {displayJenisKelamin(p.jenis_kelamin)}
                        </span>
                      </td>
                      <td style={tdStyle}>{p.no_telepon || '-'}</td>
                      <td style={tdStyle}>
                        {p.golongan_darah ? (
                          <span style={{ backgroundColor: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '700' }}>
                            {p.golongan_darah}
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' }}>
                          <button onClick={() => handleEdit(p)} style={btnActionStyle('#2563EB')}>Edit</button>
                          <button onClick={() => openDeleteModal(p)} style={btnActionStyle('#EF4444')}>Hapus</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── PAGINATION ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', background: '#FFFFFF', padding: '12px 20px', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '13px', color: '#6B7280' }}>
            Menampilkan <strong>{patients.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0}</strong> - <strong>{Math.min(currentPage * itemsPerPage, totalItems)}</strong> dari <strong>{totalItems}</strong> data
          </div>
          
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              style={{ ...btnPaginationStyle, opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              ‹ Sebelumnya
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) pageNum = i + 1;
              else if (currentPage <= 3) pageNum = i + 1;
              else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
              else pageNum = currentPage - 2 + i;
              
              const isCurrent = currentPage === pageNum;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  disabled={loading}
                  style={{
                    ...btnPaginationStyle,
                    backgroundColor: isCurrent ? '#2563EB' : '#FFFFFF',
                    color: isCurrent ? '#FFFFFF' : '#374151',
                    borderColor: isCurrent ? '#2563EB' : '#D1D5DB',
                    fontWeight: isCurrent ? '700' : '500'
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              style={{ ...btnPaginationStyle, opacity: currentPage === totalPages ? 0.5 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Selanjutnya ›
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#6B7280' }}>
            <label htmlFor="items-per-page-select">Tampilkan:</label>
            <select 
              id="items-per-page-select"
              value={itemsPerPage} 
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              disabled={loading}
              style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '13px', outline: 'none' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>per halaman</span>
          </div>
        </div>
      )}

      {/* ── MODAL FORM TAMBAH / EDIT PASIEN ── */}
      <AdminCrudModal
        open={showForm}
        title={editId ? `Edit Data Pasien (${formatPasienId(editId)})` : 'Tambah Pasien Baru'}
        onClose={() => { setShowForm(false); resetForm(); }}
      >
        <div style={{ padding: '4px' }}>
          {submitError && (
            <div style={{ padding: '10px 14px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #FECACA' }}>
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Nama Lengkap <span style={{ color: '#EF4444' }}>*</span></label>
                <input name="nama" placeholder="Nama Lengkap Pasien" value={form.nama} onChange={handleChange} required maxLength={255} style={inputStyle} />
                {renderFieldError('nama')}
              </div>

              <div>
                <label style={labelStyle}>No. Identitas (NIK/KTP) <span style={{ color: '#EF4444' }}>*</span></label>
                <input 
                  name="no_identitas" 
                  placeholder="16 Digit NIK" 
                  value={form.no_identitas} 
                  onChange={handleChange} 
                  required 
                  maxLength={16} 
                  inputMode="numeric"
                  style={inputStyle} 
                />
                <small style={{ fontSize: '11px', color: form.no_identitas.length === 16 ? '#10B981' : '#6B7280', marginTop: '2px', display: 'block' }}>
                  {form.no_identitas.length}/16 digit
                </small>
                {renderFieldError('no_identitas')}
              </div>

              <div>
                <label style={labelStyle}>Tanggal Lahir <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="date" name="tanggal_lahir" value={form.tanggal_lahir} onChange={handleChange} required style={inputStyle} />
                {renderFieldError('tanggal_lahir')}
              </div>

              <div>
                <label style={labelStyle}>Jenis Kelamin <span style={{ color: '#EF4444' }}>*</span></label>
                <select name="jenis_kelamin" value={form.jenis_kelamin} onChange={handleChange} required style={inputStyle}>
                  <option value="">-- Pilih --</option>
                  <option value="L">Laki-laki</option>
                  <option value="P">Perempuan</option>
                </select>
                {renderFieldError('jenis_kelamin')}
              </div>

              <div>
                <label style={labelStyle}>Golongan Darah</label>
                <select name="golongan_darah" value={form.golongan_darah} onChange={handleChange} style={inputStyle}>
                  <option value="">-- Pilih --</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="AB">AB</option>
                  <option value="O">O</option>
                </select>
                {renderFieldError('golongan_darah')}
              </div>

              <div>
                <label style={labelStyle}>No. Telepon <span style={{ color: '#EF4444' }}>*</span></label>
                <input name="no_telepon" placeholder="08xx..." value={form.no_telepon} onChange={handleChange} required maxLength={20} style={inputStyle} />
                {renderFieldError('no_telepon')}
              </div>

              <div>
                <label style={labelStyle}>Email</label>
                <input type="email" name="email" placeholder="email@domain.com" value={form.email} onChange={handleChange} maxLength={255} style={inputStyle} />
                {renderFieldError('email')}
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>
                  Password Akun {editId ? '(Opsional)' : '(Default: password123)'}
                </label>
                <input
                  type="password"
                  name="password"
                  placeholder={editId ? 'Kosongkan jika tidak ingin mengubah password' : 'Kosongkan untuk pakai password default (password123)'}
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  style={inputStyle}
                />
                {renderFieldError('password')}
              </div>

              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Alamat Lengkap <span style={{ color: '#EF4444' }}>*</span></label>
                <textarea name="alamat" placeholder="Alamat domisili..." value={form.alamat} onChange={handleChange} required maxLength={2000} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                {renderFieldError('alamat')}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} disabled={submitLoading} style={btnSecondaryStyle}>
                Batal
              </button>
              <button type="submit" disabled={submitLoading} style={btnPrimaryStyle}>
                {submitLoading ? 'Menyimpan...' : editId ? 'Update Data' : 'Simpan Data'}
              </button>
            </div>
          </form>
        </div>
      </AdminCrudModal>

      {/* ── MODAL KONFIRMASI HAPUS PASIEN ── */}
      <AdminCrudModal
        open={Boolean(deleteTarget)}
        title="Konfirmasi Hapus Pasien"
        onClose={closeDeleteModal}
        size="sm"
      >
        <div style={{ padding: '4px' }}>
          <p style={{ margin: '0 0 16px', color: '#374151', fontSize: '14px' }}>
            Apakah Anda yakin ingin menghapus data pasien <strong>{deleteTarget?.nama}</strong> ({formatPasienId(deleteTarget?.id, deleteTarget?.kode_pasien)})? Tindakan ini tidak dapat dibatalkan.
          </p>

          {deleteError && (
            <div style={{ padding: '10px 14px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #FECACA' }}>
              ⚠️ {deleteError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button type="button" onClick={closeDeleteModal} disabled={deleteLoading} style={btnSecondaryStyle}>
              Batal
            </button>
            <button type="button" onClick={handleDelete} disabled={deleteLoading} style={btnDangerStyle}>
              {deleteLoading ? 'Menghapus...' : 'Ya, Hapus'}
            </button>
          </div>
        </div>
      </AdminCrudModal>

    </AdminLayout>
  );
}

// ===================== STYLES =====================
const thStyle = {
  padding: '14px 16px',
  fontWeight: '700',
  fontSize: '12px',
  color: '#4B5563',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle = {
  padding: '14px 16px',
  verticalAlign: 'middle',
  fontSize: '13px',
  color: '#374151'
};

const idBadgeStyle = {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: '6px',
  backgroundColor: '#F3F4F6',
  color: '#374151',
  fontFamily: 'monospace',
  fontSize: '12px',
  fontWeight: '700',
  border: '1px solid #E5E7EB',
  letterSpacing: '0.02em'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#374151'
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  fontSize: '13px',
  outline: 'none',
  backgroundColor: '#F9FAFB',
  boxSizing: 'border-box'
};

const btnActionStyle = (bgColor) => ({
  padding: '6px 12px',
  backgroundColor: bgColor,
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '600'
});

const btnPrimaryStyle = {
  padding: '9px 16px',
  backgroundColor: '#2563EB',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnSecondaryStyle = {
  padding: '9px 16px',
  backgroundColor: '#FFFFFF',
  color: '#374151',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnDangerStyle = {
  padding: '9px 16px',
  backgroundColor: '#EF4444',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600'
};

const btnPaginationStyle = {
  padding: '6px 12px',
  fontSize: '13px',
  borderRadius: '6px',
  border: '1px solid #D1D5DB',
  backgroundColor: '#FFFFFF',
  color: '#374151',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};